export const WALL_HIT_THROTTLE_MS = 30_000;

/**
 * Hard->soft switch: past this many cumulative exposures the price wall becomes the
 * one-time free-month offer (then the quiet hint if declined).
 *
 * LOWERED 10 -> 5 on 2026-08-03 (operator decision) because the offer branch had been
 * DARK FOR ITS ENTIRE LIFE. At 10 the gate needed an 11th exposure, and measured over 90
 * days of `scheduling_wall_hit`, ZERO of 875 tracked users had ever exceeded 10 — 96% sat
 * at <=2 and the p99 was 7. So the offer UI never rendered, `startProTrial()` was never
 * called, and `pro_trial_started` / `pro_trial_ineligible` / `pro_trial_failed` were 0 all
 * time. The reported "1,646 wall hits -> 1 offer -> 0 trials" was not a broken trial; it
 * was a trial nobody had ever been offered.
 *
 * The previous value carried the comment "dose-response: converts ~0% past ~10". That is
 * not what the data says. Measured paid-conversion by wall-hit count (90d, n=1,649):
 *
 *     1 hit  7.9%  |  2  7.3%  |  3  7.1%  |  4-5  6.3%  |  6-10  10.6%  |  11-20  5.5%  |  21+  0.0% (n=21)
 *
 * Conversion decays gently and only reaches zero past 21. What DOES justify a cap is where
 * the conversions actually are: 86% of all paid conversions come from users at <=5 hits and
 * 98% from <=10, so the band past 5 contributes ~14%. Offering a trial there trades little
 * revenue for a reachable branch, and by the 6th exposure the user has declined the wall
 * five times already.
 *
 * Do not raise this above the observed exposure ceiling again — `wall-exposure.test.ts`
 * asserts reachability against the measured distribution precisely so that cannot recur.
 */
export const FATIGUE_CAP = 5;

const EXPOSURE_KEY = 'bt-wall-exposures';
const OFFER_DECLINED_KEY = 'bt-wall-offer-declined';
let lastFireAt = -Infinity; // sentinel: "never fired" so the very first call always fires
let lastSoftShownFireAt = -Infinity; // sentinel: "never fired" so the very first call always fires
let memCount = 0; // fallback when localStorage is unavailable
let memOfferDeclined = false; // fallback when localStorage is unavailable

const ls = (): Storage | null => {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
};

/** True at most once per throttle window — dedupes rapid open/close re-fires. */
export const shouldFireWallHit = (now: number): boolean => {
  if (now - lastFireAt < WALL_HIT_THROTTLE_MS) return false;
  lastFireAt = now;
  return true;
};

/** Same throttle shape as shouldFireWallHit, but for `scheduling_wall_soft_shown`
 * (the offer/hint states). Module-level (not a per-component ref) so a rapid
 * open/close remount loop can't re-fire it — the component instance resets on
 * every remount, but this module-level timestamp survives across them. */
export const shouldFireSoftShown = (now: number): boolean => {
  if (now - lastSoftShownFireAt < WALL_HIT_THROTTLE_MS) return false;
  lastSoftShownFireAt = now;
  return true;
};

export const getWallExposure = (): number => {
  const s = ls();
  if (!s) return memCount;
  return Number(s.getItem(EXPOSURE_KEY) ?? '0') || 0;
};

/** Overwrite the cached exposure count. Internal to the adopt path. */
const setWallExposure = (n: number): void => {
  const st = ls();
  if (st) {
    try { st.setItem(EXPOSURE_KEY, String(n)); } catch { memCount = n; }
  } else {
    memCount = n;
  }
};

/**
 * Adopt the SERVER's cumulative exposure count, returned on the wall-hit event.
 *
 * The offer gate used to read a per-device localStorage counter. Measured 2026-07-28 it had
 * seen 99 of 1,528 wall-hitters (6.5%) and exactly ONE user past FATIGUE_CAP, against a true
 * 75 server-side: a user on two machines never accumulated, and clearing site data reset them
 * to zero, so the branch was effectively unreachable. The server is now the source of truth
 * and localStorage is a synchronously-readable CACHE of it, refreshed on every wall hit.
 *
 * The server value WINS even when it is lower than the local one. This is a mirror, not a
 * high-water mark: treating it as max() would make a server-side reset unrecoverable.
 *
 * A null/absent/non-finite count means the server could not read it (see wall-exposure.ts on
 * the backend, which degrades rather than throwing). Leave the cache alone in that case.
 * `offerDeclined` is only ever set, never cleared, so a payload that omits it cannot resurrect
 * an offer the user already dismissed.
 */
export const adoptServerWallExposure = (
  res?: { exposures?: number | null; offerDeclined?: boolean } | null,
): void => {
  if (!res) return;
  const n = res.exposures;
  if (typeof n === 'number' && Number.isFinite(n)) setWallExposure(n);
  if (res.offerDeclined === true) setOfferDeclined();
};

export const bumpWallExposure = (): number => {
  const next = getWallExposure() + 1;
  const s = ls();
  if (s) {
    try { s.setItem(EXPOSURE_KEY, String(next)); } catch { memCount = next; }
  } else {
    memCount = next;
  }
  return next;
};

/** Which treatment to show given cumulative exposures. */
export const chooseWallTreatment = (exposures: number): 'hard' | 'soft' =>
  exposures > FATIGUE_CAP ? 'soft' : 'hard';

/** Whether the user has already declined the free-month offer (persists past-cap
 * to drop them to the quiet hint instead of re-showing the offer forever). */
export const getOfferDeclined = (): boolean => {
  const s = ls();
  return s ? s.getItem(OFFER_DECLINED_KEY) === '1' : memOfferDeclined;
};

export const setOfferDeclined = (): void => {
  const s = ls();
  if (s) {
    try { s.setItem(OFFER_DECLINED_KEY, '1'); } catch { memOfferDeclined = true; }
  } else {
    memOfferDeclined = true;
  }
};

export type WallState = 'hard' | 'offer' | 'hint';

/**
 * The scheduling free-limit trigger ALWAYS shows the real, dismissable hard Go-Pro wall.
 *
 * DROPPED (2026-08-24, operator decision) the fatigue offer/hint fallbacks. The old
 * three-way selector routed a user past `FATIGUE_CAP` cumulative exposures to a one-time
 * `'offer'` (7-day trial) and then, on any dismiss, a permanent quiet `'hint'` ("Got it").
 * Because `exposures` is a server-persisted cumulative counter that never resets and
 * `offerDeclined` is a one-way latch, a repeat scheduler was stuck on the quiet hint forever
 * and the real price wall became unreachable — and the hint converted ~0. So we always
 * return `'hard'`: the wall stays dismissable/non-blocking (the popup's own onClose), it just
 * always presents the actual upgrade offer.
 *
 * The `exposures`/`offerDeclined` inputs are retained on the signature (the caller still
 * tracks them for analytics + server sync) but no longer influence which wall shows. If a
 * very-high sanity ceiling is ever wanted, add it here — but it must still resolve to a
 * shown wall, not a silent suppression.
 */
export const chooseWallState = (_o?: { exposures: number; offerDeclined: boolean }): WallState => 'hard';

export const __resetWallExposureForTest = () => {
  lastFireAt = -Infinity; lastSoftShownFireAt = -Infinity; memCount = 0; memOfferDeclined = false;
  const s = ls(); if (s) { s.removeItem(EXPOSURE_KEY); s.removeItem(OFFER_DECLINED_KEY); }
};
